/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import {
  Table, Button, Space, Card, Form, Input, Row, Col,
  Flex, Typography, Tag, Popconfirm, Switch, message, Divider,
} from "antd";
import { PlusOutlined, EditOutlined, SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { RiDeleteBin5Line } from "react-icons/ri";
import ConsigneeService from "../../../service/ERP/Consignee.service";

const conService = ConsigneeService();

export default function CustomerConsignee({ cuscode, getSelf, readOnly = false }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const formRef = useRef(null);

  const load = async () => {
    if (!cuscode) return;
    try {
      setLoading(true);
      const res = await conService.list(cuscode);
      setData(res.data?.data || []);
    } catch {
      message.error("โหลดรายการ Consignee ไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [cuscode]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_default: false });
    setOpen(true);
    scrollToForm();
  };

  /* ฟอร์มอยู่ในหน้าเดียวกัน — เลื่อนไปให้เห็นเวลากดเพิ่ม/แก้ไข */
  const scrollToForm = () => {
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 60);
  };

  const closeForm = () => {
    setOpen(false);
    setEditing(null);
    form.resetFields();
  };

  // ติก "ที่อยู่ตัวเอง" -> ดึงข้อมูลลูกค้ามาเติมทันที, ยกเลิกติก -> ล้างช่องที่ดึงมา
  const handleSelfToggle = (checked) => {
    if (checked && getSelf) {
      const self = getSelf();
      form.setFieldsValue({ ...self, is_default: true });
    } else {
      form.setFieldsValue({ is_default: false });
    }
  };

  const openEdit = (record) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue({ ...record, is_default: Number(record.is_default) === 1 });
    setOpen(true);
    scrollToForm();
  };

  const handleSubmit = async () => {
    try {
      const v = await form.validateFields();
      const payload = { ...v, is_default: v.is_default ? 1 : 0 };
      if (editing) {
        await conService.update({ ...payload, id: editing.id });
        message.success("อัพเดท Consignee สำเร็จ");
      } else {
        await conService.create({ ...payload, cuscode });
        message.success("เพิ่ม Consignee สำเร็จ");
      }
      closeForm();
      load();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || "บันทึกไม่สำเร็จ");
    }
  };

  const handleDelete = async (record) => {
    try {
      await conService.deleted(record.id);
      message.success("ลบสำเร็จ");
      load();
    } catch {
      message.error("ลบไม่สำเร็จ");
    }
  };

  const columns = [
    {
      title: "ชื่อ Consignee", dataIndex: "consignee_name", key: "consignee_name", width: 220,
      render: (v, r) => (
        <Space>
          <span>{v || "-"}</span>
          {Number(r.is_default) === 1 && <Tag color="blue">ที่อยู่ตัวเอง</Tag>}
        </Space>
      ),
    },
    { title: "ที่อยู่", dataIndex: "address", key: "address", ellipsis: true },
    { title: "ผู้ติดต่อ", dataIndex: "contact_name", key: "contact_name", width: 140 },
    { title: "อีเมล", dataIndex: "email", key: "email", width: 170 },
    { title: "ปลายทาง", dataIndex: "final_destination", key: "final_destination", width: 140 },
    ...(readOnly
      ? []
      : [
          {
            title: "จัดการ", key: "action", width: 110, align: "center", fixed: "right",
            render: (_, record) => (
              <Space>
                <Button size="small" className="bn-primary-outline" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                <Popconfirm title="ยืนยันการลบ?" onConfirm={() => handleDelete(record)} okText="ลบ" cancelText="ยกเลิก">
                  <Button size="small" danger icon={<RiDeleteBin5Line style={{ fontSize: "1rem", marginTop: 3 }} />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]),
  ];

  return (
    <div className="pt-2">
      <Flex justify="space-between" align="center" className="px-2" style={{ marginBottom: 8 }}>
        <Typography.Title level={4} className="m-0 !text-zinc-800">Consignee / ผู้รับสินค้า</Typography.Title>
        {!readOnly && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} disabled={open && !editing}>
            เพิ่ม Consignee
          </Button>
        )}
      </Flex>

      <Table
        size="small"
        bordered
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={false}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: "ยังไม่มี Consignee" }}
      />

      <div ref={formRef}>
      {open && (
      <Card
        className="mt-3"
        title={editing ? `แก้ไข Consignee : ${editing.consignee_name || ""}` : "เพิ่ม Consignee"}
        extra={
          <Space>
            <Button icon={<CloseOutlined />} onClick={closeForm}>ยกเลิก</Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSubmit}>บันทึก</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" autoComplete="off">
          <Row gutter={[12, 0]}>
            <Col xs={24} md={16}>
              <Form.Item label="ชื่อ Consignee" name="consignee_name" rules={[{ required: true, message: "กรุณาระบุชื่อ" }]}>
                <Input placeholder="Consignee name" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="ที่อยู่ตัวเอง (ค่าเริ่มต้น)" name="is_default" valuePropName="checked">
                <Switch checkedChildren="ใช่" unCheckedChildren="ไม่" onChange={handleSelfToggle} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="ที่อยู่" name="address">
                <Input.TextArea rows={2} placeholder="Address" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="ผู้ติดต่อ" name="contact_name"><Input placeholder="Contact name" /></Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="อีเมล" name="email"><Input placeholder="E-mail" /></Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="เบอร์โทร" name="tel"><Input placeholder="Tel" /></Form.Item>
            </Col>

            <Col xs={24}>
              <Divider orientation="left" orientationMargin={0} style={{ margin: "4px 0 12px" }}>
                <Typography.Text strong>Notify Party</Typography.Text>
              </Divider>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="ชื่อ Notify party" name="notify_name"><Input placeholder="Notify party name" /></Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="ผู้ติดต่อ (Notify)" name="notify_contact"><Input placeholder="Contact name" /></Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="อีเมล (Notify)" name="notify_email"><Input placeholder="E-mail" /></Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="ที่อยู่ (Notify)" name="notify_address"><Input.TextArea rows={2} placeholder="Notify address" /></Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="Place of receipt" name="place_of_receipt"><Input placeholder="Place of receipt" /></Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Final destination" name="final_destination"><Input placeholder="Final destination" /></Form.Item>
            </Col>
          </Row>

          <Flex justify="flex-end" gap={8}>
            <Button icon={<CloseOutlined />} onClick={closeForm}>ยกเลิก</Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSubmit}>บันทึก</Button>
          </Flex>
        </Form>
      </Card>
      )}
      </div>
    </div>
  );
}
